import React, { FC, RefObject, useState } from "react";

import { Icon, TextField } from "@telia/teddy";
import Autosuggest, { BlurEvent, ChangeEvent } from "react-autosuggest";
import "./OrgOrNameInput.less";

interface Suggestion {
    name: string;
    orgNumber: string;
}
interface OrgOrNameInputProps {
    businesses: Suggestion[];
    checkOrgName: (orgName: string) => void;
    checkOrgNumber: (orgNumber: string) => void;
    initialValue?: string;
}

interface InputProps<TSuggestion> {
    id: string;
    name: string;
    onBlur?: (event: React.FocusEvent, params?: BlurEvent<TSuggestion>) => void;
    onChange: (event: React.FormEvent, newValue: ChangeEvent) => void;
    ref: RefObject<HTMLInputElement>;
    value: string;
}

const OrgOrNameInput: FC<OrgOrNameInputProps> = ({ initialValue = "", checkOrgNumber, checkOrgName, businesses }) => {
    const [value, setValue] = useState(initialValue);

    const onChange = (_event: React.FormEvent, { newValue }: ChangeEvent): React.SetStateAction<void> => {
        setValue(newValue);
    };

    const loadOrg = (orgNumber: string): void => {
        checkOrgNumber(orgNumber);
    };

    const onSuggestionsUpdateRequested = ({ value: newValue }: { value: string }): void => {
        const orgNrNoWhiteSpaces = newValue.replace(/ /g, "");
        const isOrgNumber = /^\d{9}$/.test(orgNrNoWhiteSpaces);

        if (isOrgNumber) {
            loadOrg(orgNrNoWhiteSpaces);
        } else {
            checkOrgName(newValue);
        }
    };

    const onSuggestionSelected = (_event: React.FormEvent, { suggestion }: { suggestion: Suggestion }): void => {
        const orgNumber = suggestion.orgNumber;
        loadOrg(orgNumber);
    };

    const getSuggestionValue = (suggestion: Suggestion): string => {
        return suggestion.name;
    };

    const renderSuggestion = (suggestion: Suggestion): React.ReactNode => {
        return (
            <div className="org-or-name-container__suggestion">
                <Icon className="org-or-name-container__suggestion-icon" name="premises" />
                <span>{suggestion.name}</span>
            </div>
        );
    };

    const renderInputComponent = (inputProps: InputProps<Suggestion>): React.ReactNode => {
        const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
            inputProps.onChange(event, { newValue: event.target.value, method: "type" });
        };

        return (
            <TextField>
                <TextField.Label>Selskap</TextField.Label>
                <TextField.Input
                    placeholder="Søk med bedriftsnavn eller org.nummer"
                    {...inputProps}
                    onChange={onChangeHandler}
                    ref={inputProps.ref}
                    data-di-mask
                />
            </TextField>
        );
    };

    const inputProps = {
        value: value || "",
        onChange: onChange,
        name: "orgnumber",
        id: "orgnumber",
    };
    return (
        <div className="org-or-name-container">
            <Autosuggest
                suggestions={businesses}
                onSuggestionsFetchRequested={onSuggestionsUpdateRequested}
                getSuggestionValue={getSuggestionValue}
                renderSuggestion={renderSuggestion}
                // @ts-expect-error nested 3 party error, fix later
                renderInputComponent={renderInputComponent}
                onSuggestionSelected={onSuggestionSelected}
                onSuggestionsClearRequested={(): void => undefined}
                inputProps={inputProps}
                data-di-mask
            />
        </div>
    );
};

export default OrgOrNameInput;
